import React, { useEffect, useState } from 'react';
import theme from '../../theme';
import useInstrumentFolders from './useInstrumentFolders';
import { channelOf, lastN, quickView, viewFilters } from './useNotificationsStream';

const Icon = ({ d, className = 'h-4 w-4', stroke = 1.6 }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const FlagIcon = (p) => <Icon {...p} d={<><path d="M5 5v16" /><path d="M5 5h11l-2 4l2 4H5" /></>} />;
const TrashIcon = (p) => <Icon {...p} d={<><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13" /><path d="M9 7V4h6v3" /></>} />;
const CheckIcon = (p) => <Icon {...p} d={<path d="M5 12l5 5l9-9" />} />;

export const accentFor = (n) => {
  const level = (n.level || '').toLowerCase();
  const type = (n.type || '').toLowerCase();
  const marker = n.marker || '';
  if (level === 'error' || type === 'error') return { dot: 'bg-rose-500', text: 'text-rose-700', bar: 'border-l-rose-400', soft: 'bg-rose-50' };
  // SC / Trend Extension: purple, as the 🟣 the backend marks them with.
  if (marker.includes('🟣')) return { dot: 'bg-purple-500', text: 'text-purple-700', bar: 'border-l-purple-400', soft: 'bg-purple-50' };
  if (type === 'trade' || /trade|filled|entry|exit/i.test(n.message)) return { dot: 'bg-emerald-500', text: 'text-emerald-700', bar: 'border-l-emerald-400', soft: 'bg-emerald-50' };
  if (level === 'scanner' || marker.includes('🟢') || marker.includes('🔴') || marker.includes('🟠')) return { dot: 'bg-amber-500', text: 'text-amber-700', bar: 'border-l-amber-400', soft: 'bg-amber-50' };
  if (level === 'debug' || level === 'snapshot') return { dot: 'bg-olive-300', text: 'text-olive-500', bar: 'border-l-olive-200', soft: 'bg-olive-50' };
  return { dot: 'bg-olive-400', text: 'text-olive-700', bar: 'border-l-olive-300', soft: 'bg-white' };
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return ''; }
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'flagged', label: 'Flagged' },
];

// Same split as the telegram routing: the main bot, the scanner bot and the
// debug bot. Rows written before the channel column default to main.
const CHANNELS = [
  { key: 'all', label: 'All' },
  { key: 'master', label: 'Main' },
  { key: 'scanner', label: 'Scanner' },
  { key: 'debug', label: 'Other' },
];

// Not a telegram bot: whatever the starred timeframes' hits published, any channel.
const FAVORITES = { key: 'favorites', label: 'Favorites' };

const channelLabel = (key) => CHANNELS.find((c) => c.key === key)?.label || key;

// Dashboard quick filters, by the event's own type/level (never its text), plus Flagged.
const QUICK = [
  { key: 'all', label: 'All' },
  { key: 'trades', label: 'Trades' },
  { key: 'errors', label: 'Errors' },
  { key: 'flagged', label: 'Flagged' },
];

// Only a trade (how much on which account) and an error say whose they are.
const SAYS_ACCOUNT = ['trade', 'error'];

// Starred on the account that spoke — the same rule that puts ⭐ on its telegram line.
export const isStarred = (n, stars) => stars.some((s) => (
  s.account === n.account && s.broker === n.broker && s.symbol === n.symbol
  && s.timeframes.includes(n.timeframe_meta)
));
export const matchesKind = (n, kind) => {
  if (kind === 'trades') return (n.type || '').toLowerCase() === 'trade';
  if (kind === 'errors') return (n.level || '').toLowerCase() === 'error' || (n.type || '').toLowerCase() === 'error';
  return true;
};

export default function NotificationPanel({
  title = 'Notifications',
  items = [],
  loading = false,
  unread = 0,
  filter,
  setFilter,
  onMarkAllRead,
  onToggleFlag,
  onRemove,
  onClearAll,
  onItemClick,
  defaultChannel = 'all',
  variant = 'dropdown',
  tabs = null,
  // Starred real timeframes; an array turns on the Favorites channel.
  favorites = null,
  // `broker|symbol` the favourites belong to, when the feed spans several instruments.
  favoritesPair = null,
  // Which items onItemClick applies to; all of them when not given.
  canClickItem = null,
  // Pulls this view's last FEED_SIZE from the server (useNotificationsStream.load).
  load = null,
  // All / Trades / Errors / Flagged in place of the All / Flagged row.
  quickFilters = false,
  // Stars of several instruments, [{ account, broker, symbol, timeframes }]: turns on
  // a Favorites channel spanning all of them (Dashboard Favorites).
  favoriteStars = null,
}) {
  const folders = useInstrumentFolders();
  const [confirming, setConfirming] = useState(false);
  const [channel, setChannel] = useState(defaultChannel);
  const [level, setLevel] = useState('all');
  // Quick mode picks several at once: channels OR-ed, Favorites narrowing them;
  // Trades / Errors OR-ed, Flagged (the page's `filter`) narrowing them. Empty = All.
  const [kinds, setKinds] = useState([]);
  const [picked, setPicked] = useState([]);
  const toggle = (list, key) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  const pickedChannels = picked.filter((k) => k !== FAVORITES.key);
  const favoritesOnly = picked.includes(FAVORITES.key);
  const channels = favorites || favoriteStars ? [...CHANNELS, FAVORITES] : CHANNELS;
  const inFavorites = (n) => (favoriteStars
    ? isStarred(n, favoriteStars)
    : favorites?.includes(n.timeframe_meta) && (!favoritesPair || `${n.broker}|${n.symbol}` === favoritesPair));
  const inChannel = (n) => (quickFilters
    ? (!pickedChannels.length || pickedChannels.includes(channelOf(n))) && (!favoritesOnly || inFavorites(n))
    : channel === 'all' || (channel === FAVORITES.key ? inFavorites(n) : channelOf(n) === channel));

  useEffect(() => {
    if (!confirming) return undefined;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);

  // Levels are free-form strings set at the call site, so the options come from
  // what actually arrived rather than a hardcoded list that drifts.
  const levels = [...new Set(items.map((n) => n.level).filter(Boolean))].sort();
  const view = quickFilters
    ? quickView({ pickedChannels, favoritesOnly, filter, level, favorites, favoritesPair, favoriteStars })
    : viewFilters({ channel, filter, level, favorites, favoritesPair, favoriteStars });
  const viewKey = JSON.stringify(view);
  useEffect(() => {
    if (load && view) load(view);
  }, [load, viewKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Every view is its own last FEED_SIZE, however old.
  // ponytail: Trades/Errors filter the cached items only — no server-side type filter yet.
  const visible = lastN(items, (n) => (
    (filter !== 'flagged' || n.is_flagged)
    && inChannel(n)
    && (level === 'all' || n.level === level)
    && (!kinds.length || kinds.some((k) => matchesKind(n, k)))
  ));
  const listHeight = { inline: 'max-h-72', page: 'max-h-[70vh]' }[variant] || 'max-h-96';

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-olive-100 px-3 py-2.5">
        <div className="min-w-0">
          <p className={`truncate text-sm font-semibold ${theme.textPrimary}`}>{title}</p>
          <p className={`text-[11px] ${theme.textMuted}`}>{unread} unread · {visible.length} shown</p>
        </div>
        {tabs}
        <button
          type="button"
          onClick={onMarkAllRead}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-olive-600 hover:bg-olive-50"
        >
          <CheckIcon className="h-3.5 w-3.5" /> Mark all read
        </button>
      </div>

      <div className="flex items-center gap-2 px-3 pt-2">
        <div className={`flex flex-1 gap-1 ${theme.pillContainer} rounded-xl`}>
          {quickFilters ? (
            // All clears the row; the rest toggle. Flagged is the server-side flag filter.
            QUICK.map((k) => {
              const flagged = filter === 'flagged';
              const active = k.key === 'all' ? !kinds.length && !flagged
                : k.key === 'flagged' ? flagged : kinds.includes(k.key);
              const onClick = () => {
                if (k.key === 'all') { setKinds([]); setFilter('all'); } else if (k.key === 'flagged') setFilter(flagged ? 'all' : 'flagged');
                else setKinds((list) => toggle(list, k.key));
              };
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={onClick}
                  aria-pressed={active}
                  className={`flex-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${active ? theme.pillActive : theme.pillInactive}`}
                >
                  {k.label}
                </button>
              );
            })
          ) : FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`flex-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${filter === f.key ? theme.pillActive : theme.pillInactive}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={items.length === 0}
            className="shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium text-rose-500 hover:bg-rose-50 disabled:opacity-30"
          >
            Clear all
          </button>
        ) : (
          <div className="flex shrink-0 items-center gap-1">
            <span className="text-[11px] text-olive-500">Sure?</span>
            <button
              type="button"
              onClick={() => { onClearAll(); setConfirming(false); }}
              className="rounded-lg bg-rose-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-600"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg px-2 py-1 text-[11px] font-medium text-olive-500 hover:bg-olive-50"
            >
              No
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 px-3 pt-2">
        <div className={`flex flex-1 gap-1 ${theme.pillContainer} rounded-xl`}>
          {channels.map((c) => {
            const active = quickFilters
              ? (c.key === 'all' ? !picked.length : picked.includes(c.key))
              : channel === c.key;
            const onClick = () => {
              if (!quickFilters) setChannel(c.key);
              else setPicked((list) => (c.key === 'all' ? [] : toggle(list, c.key)));
            };
            return (
              <button
                key={c.key}
                type="button"
                onClick={onClick}
                aria-pressed={quickFilters ? active : undefined}
                className={`flex-1 rounded-lg px-1.5 py-1 text-[11px] font-medium transition-colors ${active ? theme.pillActive : theme.pillInactive}`}
                title={c.key === 'all' ? 'All channels' : c === FAVORITES ? 'Starred timeframes' : `${c.label} channel`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="shrink-0 rounded-lg border border-olive-100 bg-olive-50 px-1.5 py-1 text-[11px] font-medium text-olive-600 outline-none"
          title="Log level"
        >
          <option value="all">All levels</option>
          {levels.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      <div className={`mt-2 ${listHeight} overflow-y-auto`}>
        {loading && visible.length === 0 ? (
          <p className={`px-4 py-6 text-center text-sm ${theme.textMuted}`}>Loading…</p>
        ) : visible.length === 0 ? (
          <p className={`px-4 py-6 text-center text-sm ${theme.textMuted}`}>
            {(quickFilters ? favoritesOnly : channel === FAVORITES.key)
              && !favorites?.length && !favoriteStars?.some((s) => s.timeframes?.length)
              ? 'Star timeframes in the Hits tab to fill Favorites.'
              : items.length ? 'Nothing matches these filters.' : 'Nothing here yet.'}
          </p>
        ) : (
          visible.map((n) => {
            const a = accentFor(n);
            const clickable = onItemClick && (!canClickItem || canClickItem(n));
            return (
              <div
                key={n.id}
                data-testid="feed-item"
                onClick={clickable ? () => onItemClick(n) : undefined}
                className={`group flex gap-2.5 border-l-2 ${a.bar} ${n.is_read ? 'bg-white' : a.soft} px-3 py-2.5 hover:bg-olive-50 ${clickable ? 'cursor-pointer' : ''}`}
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.dot}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className={`text-[13px] leading-snug ${n.is_read ? theme.textSecondary : theme.textPrimary} ${n.is_read ? '' : 'font-medium'}`}>
                    {n.marker ? `${n.marker} ` : ''}{n.message}
                  </p>
                  <p className={`mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] ${theme.textMuted}`}>
                    <span className={a.text}>{n.level || n.type || 'info'}</span>
                    {channelOf(n) !== 'master' && <span>· {channelLabel(channelOf(n))}</span>}
                    {n.symbol && <span>· {n.symbol}</span>}
                    {n.timeframe_meta && <span>· {n.timeframe_meta}</span>}
                    {folders[`${n.broker}|${n.symbol}`]
                      ? <span>· 📁 {folders[`${n.broker}|${n.symbol}`]}</span>
                      : n.account && SAYS_ACCOUNT.includes(n.type) && <span>· {n.account}</span>}
                    <span>· {fmtTime(n.created_at)}</span>
                  </p>
                </div>
                <div className="flex shrink-0 items-start gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleFlag(n.id); }}
                    className={`rounded-md p-1 hover:bg-olive-100 ${n.is_flagged ? 'text-amber-500' : 'text-olive-400'}`}
                    title={n.is_flagged ? 'Unflag' : 'Flag'}
                  >
                    <FlagIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(n.id); }}
                    className="rounded-md p-1 text-olive-400 hover:bg-rose-50 hover:text-rose-500"
                    title="Delete"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
